'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { create as createProto } from '@bufbuild/protobuf';
import { GenerateSkillRequestSchema } from '@/lib/protobuf/gen/skill/skill_pb';
import { sendRequest } from '@/lib/websocket';

interface SkillGeneratorDialogProps {
  onUseYaml: (yaml: string) => void;
  onClose: () => void;
}

export function SkillGeneratorDialog({ onUseYaml, onClose }: SkillGeneratorDialogProps) {
  const t = useTranslations('skills');
  const [description, setDescription] = useState('');
  const [generatedYaml, setGeneratedYaml] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setError(null);
    setGenerating(true);
    try {
      const req = createProto(GenerateSkillRequestSchema, { description });
      const resp = await sendRequest(
        { case: 'generateSkillRequest', value: req },
        'generateSkillResponse',
      );
      if (!resp.yaml) {
        setError(t('skillGeneratorDialog.noOutput'));
        return;
      }
      setGeneratedYaml(resp.yaml);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('skillGeneratorDialog.generationFailed'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogOverlay onClick={onClose} />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('skillGeneratorDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('skillGeneratorDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!generatedYaml ? (
            <>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('skillGeneratorDialog.descriptionPlaceholder')}
                className="w-full h-24 rounded-md border border-input bg-background px-3 py-2 text-sm resize-y focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              />
              {error && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}
            </>
          ) : (
            <textarea
              value={generatedYaml}
              readOnly
              className="w-full h-64 rounded-md border border-input bg-muted/50 px-3 py-2 text-sm font-mono resize-y"
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('skillGeneratorDialog.cancel')}
          </Button>
          {!generatedYaml ? (
            <Button
              size="sm"
              onClick={handleGenerate}
              disabled={generating || !description.trim()}
            >
              {generating ? t('skillGeneratorDialog.generating') : t('skillGeneratorDialog.generate')}
            </Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={() => setGeneratedYaml(null)}>
                {t('skillGeneratorDialog.tryAgain')}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  onUseYaml(generatedYaml);
                  onClose();
                }}
              >
                {t('skillGeneratorDialog.useThis')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
